import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const focusableIn = (container) =>
  [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);

/**
 * Keep Tab and Shift+Tab inside `containerRef` while `active`, move focus in
 * on open (`initialFocusRef`, else the first focusable element) and give it
 * back on close: to `returnFocusRef.current` when set (an element, or a
 * function returning one, resolved after the close renders), else to the
 * element that had focus when the trap opened.
 */
export function useFocusTrap(containerRef, active, { initialFocusRef, returnFocusRef } = {}) {
  const openerRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    openerRef.current = document.activeElement;

    const frame = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container || container.contains(document.activeElement)) return;
      const target = initialFocusRef?.current ?? focusableIn(container)[0] ?? container;
      target.focus({ preventScroll: true });
    });

    const onKeyDown = (event) => {
      const container = containerRef.current;
      if (event.key !== 'Tab' || !container) return;
      const focusable = focusableIn(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = container.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      const opener = openerRef.current;
      requestAnimationFrame(() => {
        const requested = returnFocusRef?.current;
        const target = (typeof requested === 'function' ? requested() : requested) ?? opener;
        if (target?.isConnected) target.focus({ preventScroll: true });
      });
    };
    // The refs are read when the trap opens and closes, not tracked.
  }, [active]);
}
